import { isCrmHost } from '@/pages/crm/crm-access';
import { useState, useEffect, useRef } from 'react';
import { Button, TwoFactorVerify } from '@gokrew/ui';
import { isTerminalAuthError, TerminalAuthError } from '@gokrew/ui/auth';
import { useDialog } from '@gokrew/ui/context';
import { getApiErrorMessage } from '@gokrew/ui/utility/utility';
import { Form } from 'formik';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { useTranslation } from 'react-i18next';
import { TranslationNS_Admin } from '@gokrew/models/constants';
import { KollabHRLogo } from '@gokrew/ui/assets';
import { loginSchema } from '@gokrew/ui/validation/authSchema';
import { useAuth } from '@/context/AuthContext';
import { resendOtp, verifyOtp } from '@/api/auth';

interface AdminLoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation(TranslationNS_Admin.Auth);
  const [showPassword, setShowPassword] = useState(false);
  const { t: crmT } = useTranslation(TranslationNS_Admin.CompanyInfo);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, commitAccessToken, isLoading, isAuthenticated } = useAuth();
  const { openDialog, closeDialog, dialogs } = useDialog();
  const dialogIdRef = useRef<string | null>(null);
  const [awaiting2FA, setAwaiting2FA] = useState(false);
  const pendingOtpTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Gate the post-login redirect behind 2FA — don't navigate anywhere
    // until it's cleared.
    if (!isAuthenticated || awaiting2FA) return;

    const redirect = searchParams.get('redirect');

    // Only follow same-origin relative paths — a bare `redirect=` from the
    // URL is attacker-controlled, and `//evil.com` is parsed as an
    // external origin by browsers despite starting with a single slash.
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      navigate(redirect);
    } else {
      navigate('/admins');
    }
  }, [isAuthenticated, awaiting2FA, navigate, searchParams]);

  // The dialog's own header close button (and backdrop/escape) close it
  // directly via closeDialog, bypassing closeTwoFactorDialog below — watch
  // for that so awaiting2FA doesn't get stuck true and block the redirect.
  useEffect(() => {
    if (
      awaiting2FA &&
      dialogIdRef.current &&
      !dialogs.some((d) => d.id === dialogIdRef.current)
    ) {
      dialogIdRef.current = null;
      setAwaiting2FA(false);
    }
  }, [dialogs, awaiting2FA]);

  function closeTwoFactorDialog() {
    if (dialogIdRef.current) {
      closeDialog(dialogIdRef.current);
      dialogIdRef.current = null;
    }
    pendingOtpTokenRef.current = null;
    setAwaiting2FA(false);
  }

  async function handleVerify(code: string): Promise<void> {
    if (!pendingOtpTokenRef.current) return;
    try {
      const result = await verifyOtp(pendingOtpTokenRef.current, code);
      commitAccessToken(result.data.accessToken);
      closeTwoFactorDialog();
    } catch (err) {
      const message = getApiErrorMessage(err, t('two_factor.invalid_code'));
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  async function handleResend(): Promise<void> {
    if (!pendingOtpTokenRef.current) return;
    try {
      await resendOtp(pendingOtpTokenRef.current);
    } catch (err) {
      const message = getApiErrorMessage(err, t('two_factor.resend_error'));
      throw isTerminalAuthError(err)
        ? new TerminalAuthError(message)
        : new Error(message);
    }
  }

  function openTwoFactorDialog(maskedEmail: string, otpToken: string) {
    setAwaiting2FA(true);
    pendingOtpTokenRef.current = otpToken;
    dialogIdRef.current = openDialog({
      title: t('two_factor.heading'),
      containerClass: 'w-[480px] max-w-[480px]',
      content: (
        <TwoFactorVerify
          email={maskedEmail}
          onVerify={handleVerify}
          onResend={handleResend}
          onBack={closeTwoFactorDialog}
          flat
          hideHeading
        />
      ),
    });
  }

  return (
    <div className="bg-auth-card sm:rounded-2xl p-8 sm:p-12 max-sm:min-h-screen">
      <div className="text-center mb-8">
        <div className="flex justify-center w-full">
          <img className="w-20 mb-4" src={KollabHRLogo} alt="Go-HR" />
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {isCrmHost
            ? crmT('crm.workspace.sales_workspace')
            : t('login_page.heading')}
        </h2>

        <p className="text-gray-600">
          {isCrmHost ? crmT('crm.first_visit') : t('login_page.sub_heading')}
        </p>
      </div>

      <FormikWithSchema<AdminLoginForm>
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={loginSchema}
        onSubmit={async (values) => {
          const data = await login(values);
          if ('requiresOtp' in data.data) {
            openTwoFactorDialog(data.data.maskedEmail, data.data.otpToken);
          }
          // else: tokens already committed by login() above — the
          // isAuthenticated effect handles the redirect.
        }}
      >
        {() => (
          <Form className="flex flex-col">
            <FormField
              name="email"
              type="email"
              label={t('form.email_label')}
              placeholder={t('form.email_placeholder')}
            />

            <FormField
              name="password"
              type={showPassword ? 'text' : 'password'}
              label={t('form.password_label')}
              placeholder={t('form.password_placeholder')}
              onIconClick={() => setShowPassword((v) => !v)}
            />

            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-700 ml-auto"
            >
              {t('forgot_password.forgot_password')}
            </Link>

            <Button
              variant="primary"
              type="submit"
              className="w-full transition-opacity duration-200 mt-5"
              disabled={isLoading}
            >
              {isLoading ? t('login_page.logging_in') : t('login_page.login')}
            </Button>
          </Form>
        )}
      </FormikWithSchema>
    </div>
  );
}
